import { useForm } from 'react-hook-form'
import { Upload, X, User } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '../common'
import { getFullLogoUrl } from '../../utils/urlUtils'
import { roleService } from '../../services/roleService'
import { companyService } from '../../services/companyService'

function UserForm({ 
  isOpen, 
  editingUser, 
  onClose, 
  onSubmit, 
  isLoading,
  selectedAvatar,
  avatarPreview,
  onAvatarSelect,
  onAvatarUpload,
  onAvatarDelete,
  onRemoveAvatarPreview,
  isUploadingAvatar
}) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  const handleSubmitForm = (data) => {
    // Remove confirmPassword from the data before submitting
    const { confirmPassword, ...submitData } = data;
    onSubmit(submitData);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Fetch roles
  const fetchRoles = async () => {
    setIsLoadingRoles(true);
    try {
      const response = await roleService.getAll();
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  // Fetch companies
  const fetchCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      const response = await companyService.getAll();
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  // Fetch roles and companies when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchRoles();
      fetchCompanies();
    }
  }, [isOpen]);

  // Reset form when editing user changes
  useEffect(() => {
    if (editingUser) {
      reset({
        firstName: editingUser.firstName || "",
        lastName: editingUser.lastName || "",
        email: editingUser.email || "",
        phone: editingUser.phone || "",
        address: editingUser.address || "",
        city: editingUser.city || "",
        state: editingUser.state || "",
        pincode: editingUser.pincode || "",
        roleId: editingUser.roleId || "",
        companyId: editingUser.companyId || "",
        password: "",
        confirmPassword: "",
        isActive: editingUser.isActive !== undefined ? editingUser.isActive : true,
      });
    } else {
      reset({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        roleId: "",
        companyId: "",
        password: "",
        confirmPassword: "",
        isActive: true,
      });
    }
  }, [editingUser, reset]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="modal-overlay" onClick={handleClose} />
        <div className="modal-content max-w-2xl">
          <div className="modal-header">
            <h3 className="text-lg font-semibold text-secondary-900">
              {editingUser ? "Edit User" : "Add New User"}
            </h3>
            <button
              onClick={handleClose}
              className="text-secondary-400 hover:text-secondary-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-6">
            <div className="modal-body">
              {/* Avatar Section */}
              <div className="mb-6">
                <label className="block text-base font-medium text-secondary-700 mb-2">
                  Avatar
                </label>
                <div className="flex items-center space-x-4">
                  {avatarPreview || editingUser?.avatar ? (
                    <div className="relative">
                      <img
                        src={getFullLogoUrl(editingUser?.avatar)}
                        alt="Current avatar"
                        className="h-16 w-16 object-cover rounded-lg border border-secondary-200"
                      />
                      <button
                        type="button"
                        onClick={() => onAvatarDelete(editingUser?.id)}
                        className="absolute -top-2 -right-2 bg-danger-500 text-white rounded-full p-1 hover:bg-danger-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-16 w-16 bg-secondary-100 rounded-lg flex items-center justify-center">
                      <User className="h-8 w-8 text-secondary-400" />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <label className="btn-outline cursor-pointer inline-flex items-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={onAvatarSelect}
                        className="hidden"
                      />
                      <Upload className="h-4 w-4 mr-2" />
                      {avatarPreview || editingUser?.avatar ? "Change Avatar" : "Upload Avatar"}
                    </label>
                    {selectedAvatar && (
                      <button
                        type="button"
                        onClick={() => onAvatarUpload(editingUser?.id)}
                        className="btn-primary ml-2"
                        disabled={isUploadingAvatar}
                      >
                        {isUploadingAvatar ? "Uploading..." : "Upload"}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    {...register("firstName", { required: "First name is required" })}
                    className="input"
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <p className="text-danger-600 text-base mt-1">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    {...register("lastName", { required: "Last name is required" })}
                    className="input"
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <p className="text-danger-600 text-base mt-1">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    {...register("email", { 
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address"
                      }
                    })}
                    className="input"
                    placeholder="Enter email address"
                  />
                  {errors.email && (
                    <p className="text-danger-600 text-base mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    {...register("phone")}
                    className="input"
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              {/* Password Fields - Only show for new users or when editing */}
              {!editingUser && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      {...register("password", { 
                        required: !editingUser ? "Password is required" : false,
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters"
                        }
                      })}
                      className="input"
                      placeholder="Enter password"
                    />
                    {errors.password && (
                      <p className="text-danger-600 text-base mt-1">
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      {...register("confirmPassword", { 
                        required: !editingUser ? "Please confirm your password" : false,
                        validate: (value, { password }) => 
                          !editingUser && value !== password ? "Passwords do not match" : true
                      })}
                      className="input"
                      placeholder="Confirm password"
                    />
                    {errors.confirmPassword && (
                      <p className="text-danger-600 text-base mt-1">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Password Requirements Note */}
              {!editingUser && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-base text-blue-800">
                    <strong>Password Requirements:</strong> Minimum 6 characters. Choose a strong password for security.
                  </p>
                </div>
              )}

              {/* Address Information */}
              <div>
                <label className="block text-base font-medium text-secondary-700 mb-1">
                  Address
                </label>
                <textarea
                  {...register("address")}
                  className="input"
                  rows={3}
                  placeholder="Enter address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    {...register("city")}
                    className="input"
                    placeholder="Enter city"
                  />
                </div>

                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    {...register("state")}
                    className="input"
                    placeholder="Enter state"
                  />
                </div>

                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Pincode
                  </label>
                  <input
                    type="text"
                    {...register("pincode")}
                    className="input"
                    placeholder="Enter pincode"
                  />
                </div>
              </div>

              {/* Role, Company and Status */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Role *
                  </label>
                  <select 
                    {...register("roleId", { required: "Role is required" })} 
                    className="input"
                    disabled={isLoadingRoles}
                  >
                    <option value="">{isLoadingRoles ? "Loading roles..." : "Select Role"}</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  {errors.roleId && (
                    <p className="text-danger-600 text-base mt-1">
                      {errors.roleId.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Company *
                  </label>
                  <select 
                    {...register("companyId", { required: "Company is required" })} 
                    className="input"
                    disabled={isLoadingCompanies}
                  >
                    <option value="">{isLoadingCompanies ? "Loading companies..." : "Select Company"}</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  {errors.companyId && (
                    <p className="text-danger-600 text-base mt-1">
                      {errors.companyId.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Status
                  </label>
                  <select {...register("isActive")} className="input">
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <Button
                type="button"
                onClick={handleClose}
                variant="outline"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isLoading}
              >
                {isLoading
                  ? editingUser
                    ? "Updating..."
                    : "Creating..."
                  : editingUser
                  ? "Update User"
                  : "Create User"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default UserForm;
